"use client";

import Link from "next/link";
import { Container } from "@/components/Container";
import { solutions } from "@/lib/solutions";
import { MaskReveal, RevealGroup, RevealItem } from "@/components/Motion";
import { CmsText, cmsImg } from "@/components/CmsText";

type Product = {
  name: string;
  descriptor: string;
  image: string;
  href: string;
};

const products: Product[] = [
  {
    name: "Custom-Built Managed Offices",
    descriptor:
      "Enterprise-focused managed offices that bring lease, design, build, and operations into one connected model for greater consistency, control, and scalability.",
    image: "/images/products/custom-built.jpg",
    href: "/solutions/custom-built",
  },
  {
    name: "Ready-to-Move-in Suites",
    descriptor:
      "Private office spaces designed for enterprises that need speed, privacy, service, and consistency without waiting through traditional office fit-outs.",
    image: "/images/products/suites.jpg",
    href: "/solutions/suites",
  },
  {
    name: "DESYN by Table Space",
    descriptor:
      "Enterprise-focused Design & Build partner that ensures your design intent, cost transparency, and delivery timelines remain consistent.",
    image: "/images/products/desyn.jpg",
    href: "/solutions/desyn",
  },
  {
    name: "Global Connect by Table Space",
    descriptor:
      "A single integrated platform that enables global enterprises to establish, operate, and scale Global Capability Centers in India.",
    image: "/images/products/global-connect.jpg",
    href: "/solutions/global-connect",
  },
  {
    name: "Table Space Enterprise Services",
    descriptor:
      "Elevate your business with end-to-end solutions designed to go beyond your desk from hospitality and experience, productivity services, to workplace services",
    image: "/images/products/enterprise-services.jpg",
    href: "/solutions/enterprise-services",
  },
];

export function Products({ c = {} }: { c?: Record<string, string> } = {}) {
  return (
    <section className="border-t border-brand-line bg-brand-paper">
      <Container className="py-20 md:py-24">
        <div className="mb-12 md:mb-16">
          <h2 className="t-display text-brand-ink">
            <MaskReveal lines={[<span key="a">Our <span className="text-brand-accent">Solutions</span></span>]} />
          </h2>
        </div>
        <RevealGroup
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
          stagger={0.07}
        >
          {/* Retired solutions drop out here (lib/solutions → `retired`), but
              the CMS index is taken from the source position, not the filtered
              one — these keys are positional, so re-numbering them would move
              every saved override onto the wrong card. */}
          {products
            .map((p, cmsIndex) => ({ p, cmsIndex }))
            .filter(({ p }) => {
              const slug = p.href.replace("/solutions/", "");
              return !solutions.find((s) => s.slug === slug)?.retired;
            })
            .map(({ p, cmsIndex: i }) => (
            <RevealItem key={p.name} className="h-full">
              {/* white card; equal height (grid stretch) with the image full-bleed
                  at the bottom so every card's image bottom-aligns */}
              <Link
                href={p.href}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_2px_18px_-8px_rgba(0,0,0,0.10)] transition-all duration-300 hover:border-brand-accent hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.18)]"
              >
                <div className="px-6 pt-6 pb-5">
                  <h3 className="text-lg font-semibold text-brand-ink transition-colors duration-300 group-hover:text-brand-accent">
                    <CmsText c={c} k={`products.${i}.name`}>{p.name}</CmsText>
                  </h3>
                  <CmsText c={c} k={`products.${i}.descriptor`} as="p" className="mt-2 text-base font-normal leading-relaxed text-brand-slate">
                    {p.descriptor}
                  </CmsText>
                </div>
                {/* fixed-ratio image, bottom-pinned (mt-auto) so every card's
                    image is the same size and bottom-aligns across the grid; the
                    ratio is a touch taller now that the icon is gone. */}
                <div className="mt-auto aspect-[16/11] w-full overflow-hidden">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={cmsImg(c, `products.${i}.image`, p.image)}
                    alt={c[`products.${i}.image.alt`]?.trim() || p.name}
                    data-cms-img={`products.${i}.image`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] [filter:contrast(1.03)_saturate(1.04)]"
                  />
                </div>
              </Link>
            </RevealItem>
            ))}
        </RevealGroup>
      </Container>
    </section>
  );
}
